import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Box } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { LuChevronDown } from 'react-icons/lu';
import { MARKETPLACE_CATEGORY_ALL } from '../../utils/marketplace/marketplaceKeywordSearch';
import { FONT_SIZES } from '../../Theme';
import {
  getHeaderCategoryColumns,
  getHeaderFormatItems,
  getHeaderPlatformItems,
} from '../../utils/marketplace/headerMarketplaceFacetItems';
import {
  HEADER_SEARCH_CONTAINER_NAME,
  HeaderAllCategoriesRoot,
  HeaderAllCategoriesTrigger,
} from '../../styles/navigation/HeaderStyles';
import HeaderFacetLinkList from './headerFacetLinkList';

const MENU_VIEWPORT_GAP_PX = 24;
/** Matches the attached-menu fallback: calc(100dvh - 140px). */
const MENU_FALLBACK_OFFSET_PX = 140;
const MENU_CLOSE_MS = 280;
const MENU_SLIDE_EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function readMenuMaxHeight(anchor) {
  if (!anchor) return null;
  const { bottom } = anchor.getBoundingClientRect();
  const available = Math.floor(window.innerHeight - bottom - MENU_VIEWPORT_GAP_PX);
  if (available > 0) return available;
  // A trigger at the bottom of the viewport must not invert this into a negative cap.
  const fallback = Math.floor(window.innerHeight - MENU_FALLBACK_OFFSET_PX);
  return fallback > 0 ? fallback : null;
}

function MenuColumn({
  heading,
  items,
  onSelect,
  onHeadingClick,
  testIdPrefix,
}) {
  return (
    <Box
      component="section"
      aria-label={heading}
      sx={{
        minWidth: 0,
        flex: '1 1 140px',
        maxWidth: 180,
        [`@container ${HEADER_SEARCH_CONTAINER_NAME} (max-width: 560px)`]: {
          flex: '1 1 calc(50% - 8px)',
          maxWidth: 'none',
        },
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}
    >
      <Box
        component="button"
        type="button"
        data-testid={`${testIdPrefix}-heading`}
        onClick={onHeadingClick}
        sx={{
          appearance: 'none',
          border: 0,
          background: 'transparent',
          m: 0,
          mb: 0.5,
          p: 0,
          font: 'inherit',
          fontSize: FONT_SIZES.bodySm,
          fontWeight: 700,
          lineHeight: 1.25,
          color: 'text.primary',
          textAlign: 'left',
          cursor: 'pointer',
          '&:hover': {
            color: 'primary.main',
            textDecoration: 'underline',
          },
          '&:focus-visible': {
            outline: (theme) => `2px solid ${theme.palette.primary.main}`,
            outlineOffset: 2,
          },
        }}
      >
        {heading}
      </Box>
      <HeaderFacetLinkList
        items={items}
        onSelect={onSelect}
        testIdPrefix={testIdPrefix}
        listSx={{ gap: 0.2 }}
      />
    </Box>
  );
}

function HeaderAllCategories({
  userType = null,
  menuAnchorRef = null,
  closeWhen = false,
  onSelectCategory = () => {},
  onSelectPlatform = () => {},
  onSelectContentFormat = () => {},
  onBrowseAll = () => {},
  onOpenChange = () => {},
}) {
  const [open, setOpen] = useState(false);
  const [exiting, setExiting] = useState(false);
  const [menuMaxHeight, setMenuMaxHeight] = useState(null);
  const [slideHeight, setSlideHeight] = useState(0);
  const rootRef = useRef(null);
  const menuRef = useRef(null);
  const onOpenChangeRef = useRef(onOpenChange);
  const openedRef = useRef(false);
  onOpenChangeRef.current = onOpenChange;

  const closeMenu = useCallback(() => {
    setOpen(false);
    if (!prefersReducedMotion()) {
      setExiting(true);
    }
  }, []);

  const toggleMenu = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    if (open) {
      closeMenu();
      return;
    }
    setExiting(false);
    setSlideHeight(0);
    setOpen(true);
  }, [closeMenu, open]);

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event) => {
      const { target } = event;
      if (rootRef.current?.contains(target) || menuRef.current?.contains(target)) {
        return;
      }
      closeMenu();
    };

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeMenu();
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [closeMenu, open]);

  useEffect(() => {
    if (closeWhen && open) {
      closeMenu();
    }
  }, [closeMenu, closeWhen, open]);

  useEffect(() => {
    if (open) {
      openedRef.current = true;
      onOpenChangeRef.current(true);
      return undefined;
    }
    if (!openedRef.current) return undefined;

    if (prefersReducedMotion()) {
      openedRef.current = false;
      setExiting(false);
      onOpenChangeRef.current(false);
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      openedRef.current = false;
      setExiting(false);
      onOpenChangeRef.current(false);
    }, MENU_CLOSE_MS);
    return () => window.clearTimeout(timeoutId);
  }, [open]);

  // Close waits out the slide so the search card stays expanded during the
  // animation. Unmount drops that timer, so tell the parent immediately.
  useLayoutEffect(() => () => {
    if (!openedRef.current) return;
    openedRef.current = false;
    onOpenChangeRef.current(false);
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;
    const cap = menuAnchorRef ? readMenuMaxHeight(rootRef.current) : null;
    if (menuAnchorRef) setMenuMaxHeight(cap);
    const node = menuRef.current;
    if (!node) return undefined;
    const full = node.scrollHeight;
    const target = cap == null ? full : Math.min(full, cap);

    if (prefersReducedMotion()) {
      setSlideHeight(target);
      return undefined;
    }

    // The collapsed height has to be committed before the target, or the
    // height transition has no start frame and the panel pops open.
    let innerFrame = 0;
    const outerFrame = window.requestAnimationFrame(() => {
      innerFrame = window.requestAnimationFrame(() => {
        setSlideHeight(target);
      });
    });
    return () => {
      window.cancelAnimationFrame(outerFrame);
      window.cancelAnimationFrame(innerFrame);
    };
  }, [open, menuAnchorRef]);

  const handleSelectCategory = (value) => {
    onSelectCategory(value || MARKETPLACE_CATEGORY_ALL);
    closeMenu();
  };

  const handleSelectPlatform = (value) => {
    onSelectPlatform(value);
    closeMenu();
  };

  const handleSelectFormat = (value) => {
    onSelectContentFormat(value);
    closeMenu();
  };

  const handleBrowseAll = () => {
    onBrowseAll();
    closeMenu();
  };

  const categoryColumns = getHeaderCategoryColumns(userType);
  const platformItems = getHeaderPlatformItems();
  const formatItems = getHeaderFormatItems();
  const menuHost = menuAnchorRef?.current;

  const attachedToSearch = Boolean(menuHost);
  let menuSx = {
    position: 'absolute',
    top: 'calc(100% + 8px)',
    left: 0,
    zIndex: 1,
    width: '100%',
    minWidth: 0,
    maxWidth: 'min(760px, calc(100vw - 24px))',
    bgcolor: 'background.paper',
    borderRadius: (theme) => theme.spacing(3),
    border: 1,
    borderColor: 'divider',
    px: { xs: 2, md: 2.5 },
    pt: { xs: 2, md: 2.25 },
    pb: { xs: 1.5, md: 1.75 },
    display: 'flex',
    flexDirection: 'column',
    gap: 1.5,
    boxSizing: 'border-box',
  };
  if (attachedToSearch) {
    menuSx = {
      position: 'relative',
      top: 0,
      left: 0,
      zIndex: 1,
      width: '100%',
      minWidth: 0,
      maxWidth: 'none',
      bgcolor: 'transparent',
      border: 0,
      px: 0.5,
      pt: 1.25,
      pb: 0.5,
      display: 'flex',
      flexDirection: 'column',
      gap: 1,
      boxSizing: 'border-box',
      maxHeight: menuMaxHeight == null ? 'calc(100dvh - 140px)' : `${menuMaxHeight}px`,
      overflowY: 'auto',
      overscrollBehavior: 'contain',
    };
  }

  const menuShown = open || exiting;
  let menuHeight = 0;
  if (open) {
    menuHeight = `${slideHeight}px`;
  }
  const menu = menuShown ? (
    <Box
      sx={{
        height: menuHeight,
        overflow: 'hidden',
        ...(attachedToSearch ? {} : {
          position: 'absolute',
          top: 'calc(100% + 8px)',
          left: 0,
          zIndex: 1,
          width: '100%',
          maxWidth: 'min(760px, calc(100vw - 24px))',
        }),
        transition: `height ${MENU_CLOSE_MS}ms ${MENU_SLIDE_EASE}`,
        '@media (prefers-reduced-motion: reduce)': {
          transition: 'none',
        },
      }}
    >
      <Box
        ref={menuRef}
        role="menu"
        aria-label="All Categories menu"
        aria-hidden={open ? undefined : true}
        data-testid="header-all-categories-menu"
        data-placement="below-search"
        sx={{
          ...menuSx,
          ...(attachedToSearch ? {} : { position: 'relative', top: 0, left: 0 }),
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'flex-start',
            gap: { xs: 2, md: 2.5 },
          }}
        >
          <MenuColumn
            heading="Top categories"
            items={categoryColumns.top}
            onSelect={handleSelectCategory}
            onHeadingClick={() => handleSelectCategory(MARKETPLACE_CATEGORY_ALL)}
            testIdPrefix="header-all-categories-top"
          />
          <MenuColumn
            heading="More categories"
            items={categoryColumns.more}
            onSelect={handleSelectCategory}
            onHeadingClick={() => handleSelectCategory(MARKETPLACE_CATEGORY_ALL)}
            testIdPrefix="header-all-categories-more"
          />
          <MenuColumn
            heading="Platforms"
            items={platformItems}
            onSelect={handleSelectPlatform}
            onHeadingClick={() => handleSelectPlatform('')}
            testIdPrefix="header-all-categories-platform"
          />
          <MenuColumn
            heading="Format"
            items={formatItems}
            onSelect={handleSelectFormat}
            onHeadingClick={() => handleSelectFormat('')}
            testIdPrefix="header-all-categories-format"
          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            pt: 0,
            borderTop: 1,
            borderColor: 'divider',
          }}
        >
          <Box
            component="button"
            type="button"
            data-testid="header-all-categories-browse-all"
            onClick={handleBrowseAll}
            sx={{
              appearance: 'none',
              border: 0,
              m: 0,
              mt: 0.75,
              px: 2.5,
              py: 0.75,
              font: 'inherit',
              fontSize: FONT_SIZES.bodySm,
              fontWeight: 500,
              lineHeight: 1.25,
              color: 'text.primary',
              cursor: 'pointer',
              borderRadius: 999,
              bgcolor: 'background.soft',
              '&:hover': {
                bgcolor: (theme) => alpha(theme.palette.text.primary, 0.08),
              },
              '&:focus-visible': {
                outline: (theme) => `2px solid ${theme.palette.primary.main}`,
                outlineOffset: 2,
              },
            }}
          >
            Browse all
          </Box>
        </Box>
      </Box>
    </Box>
  ) : null;

  return (
    <HeaderAllCategoriesRoot ref={rootRef} data-testid="header-all-categories">
      <HeaderAllCategoriesTrigger
        type="button"
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="All Categories"
        data-testid="header-all-categories-trigger"
        $open={open}
        onClick={toggleMenu}
      >
        <span className="header-all-categories-label-full" aria-hidden>
          All Categories
        </span>
        <span className="header-all-categories-label-short" aria-hidden>
          Categories
        </span>
        <LuChevronDown aria-hidden className="header-all-categories-chevron" strokeWidth={2} />
      </HeaderAllCategoriesTrigger>
      {menuHost ? createPortal(menu, menuHost) : menu}
    </HeaderAllCategoriesRoot>
  );
}

export default HeaderAllCategories;
